import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Container } from "reactstrap";
import styled from "styled-components";
import { artifactQualityMessage, artifactUsable } from "../../utils/artifactQuality";

import CoverPreview from "../../components/CoverPreview/CoverPreview";
import NewFooter from "../../components/Footers/NewFooter";
import NewNavbar from "../../components/Navbar/NewNavbar";
import VideoPreviewCard from "../../components/VideoPreviewCard/VideoPreviewCard";
import { API_URL } from "../../constants";
import { publicApprovedVideoPreview } from "../../utils/videoPreview";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #eef5f4 0%, #f9fafb 320px);
`;

const Main = styled.main`
  flex: 1;
  padding: 120px 0 70px;
`;

const Intro = styled.div`
  max-width: 760px;
  margin: 0 auto 28px;
  text-align: center;
`;

const Eyebrow = styled.p`
  margin: 0 0 8px;
  color: #0e7e7a;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #1a1a1a;
  font-size: 1.9rem;
`;

const Note = styled.p`
  color: #6b7280;
`;

const Cover = styled.div`
  max-width: 780px;
  margin: 0 auto 28px;
`;

const Downloads = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Download = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 999px;
  background: #0e7e7a;
  color: #ffffff;
  font-weight: 700;
  text-decoration: none;
  border: 0;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #0b605d;
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const Centered = styled.div`
  padding: 80px 20px;
  text-align: center;
  color: #4b5563;
`;

const DownloadError = styled.p`
  margin: 14px auto 0;
  max-width: 680px;
  text-align: center;
  color: #b91c1c;
`;

// The Free-plan mark on the web view (D11), matching the worker's PDF mark:
// a faint diagonal wordmark (~10% ink, −32°) over the document plus a quiet
// footer line. CSS only; nothing credits-shaped reaches this page.
const Watermark = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;
  user-select: none;
  z-index: 5;

  span {
    transform: rotate(-32deg);
    font-size: clamp(48px, 9vw, 120px);
    font-weight: 800;
    letter-spacing: 0.04em;
    color: rgba(11, 28, 30, 0.1);
    white-space: nowrap;
  }
`;

const WatermarkFooter = styled.p`
  margin: 28px 0 0;
  text-align: center;
  color: rgba(11, 28, 30, 0.55);
  font-size: 0.85rem;
`;

/** @param {string} message */
const localDownloadError = (message) => Object.assign(new Error(message), { safeMessage: message });

/** @param {string} url @param {string} filename */
const triggerBrowserDownload = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const SharedReportPage = () => {
  /** @type {{ token: string }} */
  const { token } = useParams();
  const activeTokenRef = useRef(token);
  activeTokenRef.current = token;
  const loadedTokenRef = useRef(/** @type {string | null} */ (null));
  const [docState, setDoc] = useState(/** @type {Record<string, any> | null} */ (null));
  const [statusState, setStatus] = useState("loading");
  const routeStateIsCurrent = loadedTokenRef.current === token;
  const doc = routeStateIsCurrent ? docState : null;
  const status = routeStateIsCurrent ? statusState : "loading";
  const [downloadBusy, setDownloadBusy] = useState(/** @type {string | null} */ (null));
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const requestToken = token;
    // A :token change reuses this component. Remove the previous capability's
    // document and action state immediately so it is never shown under the new
    // URL while that report is loading.
    loadedTokenRef.current = requestToken;
    setDoc(null);
    setStatus("loading");
    setDownloadBusy(null);
    setDownloadError("");
    axios
      .get(`${API_URL}/shared-reports/${encodeURIComponent(token)}/`)
      .then((response) => {
        if (cancelled || activeTokenRef.current !== requestToken) return;
        loadedTokenRef.current = requestToken;
        setDoc(response.data);
        setStatus("ok");
      })
      .catch((error) => {
        if (cancelled || activeTokenRef.current !== requestToken) return;
        loadedTokenRef.current = requestToken;
        if (error.response?.status === 409) {
          setDownloadError(
            "The agent published a newer version. Ask the agent for a current link."
          );
          setStatus("stale");
        } else {
          setStatus(error.response?.status === 404 ? "not_found" : "error");
        }
      });
    return () => {
      cancelled = true;
      if (activeTokenRef.current === requestToken) activeTokenRef.current = "";
    };
  }, [token]);

  /**
   * Re-read the token-scoped report immediately before each download. This
   * catches revocation/version changes while a recipient leaves the page open,
   * and avoids treating any page-load artifact URL as permanent.
   * @param {{key: string, filename: string}} output
   */
  const downloadArtifact = async (output) => {
    const requestToken = token;
    setDownloadBusy(output.key);
    setDownloadError("");
    try {
      const response = await axios.get(`${API_URL}/shared-reports/${encodeURIComponent(token)}/`);
      if (activeTokenRef.current !== requestToken) return;
      const freshDoc = response.data || {};
      setDoc(freshDoc);
      const artifact = freshDoc.artifacts?.[output.key];
      if (!artifactUsable(artifact, output.key) || !artifact.url) {
        throw localDownloadError(
          "That file is no longer available. Ask the agent for a current share link."
        );
      }
      triggerBrowserDownload(artifact.url, output.filename);
    } catch (error) {
      if (activeTokenRef.current !== requestToken) return;
      const typed = /** @type {{safeMessage?: unknown, response?: {status?: unknown}}} */ (error);
      if (typed.response?.status === 404) {
        setDoc(null);
        setStatus("not_found");
        setDownloadError(
          "This share link was revoked or expired. Ask the agent for a current link."
        );
      } else if (typed.response?.status === 409) {
        setDoc(null);
        setStatus("stale");
        setDownloadError("The agent published a newer version. Ask the agent for a current link.");
      } else {
        setDownloadError(
          typeof typed.safeMessage === "string"
            ? typed.safeMessage
            : "Could not refresh that file. Check your connection, then try again."
        );
      }
    } finally {
      if (activeTokenRef.current === requestToken) setDownloadBusy(null);
    }
  };

  const downloadVideo = async () => {
    const requestToken = token;
    setDownloadBusy("video");
    setDownloadError("");
    try {
      const response = await axios.get(`${API_URL}/shared-reports/${encodeURIComponent(token)}/`);
      if (activeTokenRef.current !== requestToken) return;
      const freshDoc = response.data || {};
      setDoc(freshDoc);
      const approvedPreview = publicApprovedVideoPreview(freshDoc);
      const videoUrl =
        approvedPreview?.final_video?.download_url || approvedPreview?.final_video?.play_url;
      if (!videoUrl) {
        throw localDownloadError(
          "That walkthrough is no longer approved and current. Ask the agent for a current link."
        );
      }
      triggerBrowserDownload(videoUrl, "listing-walkthrough.mp4");
    } catch (error) {
      if (activeTokenRef.current !== requestToken) return;
      const typed = /** @type {{safeMessage?: unknown, response?: {status?: unknown}}} */ (error);
      if (typed.response?.status === 404) {
        setDoc(null);
        setStatus("not_found");
        setDownloadError(
          "This share link was revoked or expired. Ask the agent for a current link."
        );
      } else if (typed.response?.status === 409) {
        setDoc(null);
        setStatus("stale");
        setDownloadError("The agent published a newer version. Ask the agent for a current link.");
      } else {
        setDownloadError(
          typeof typed.safeMessage === "string"
            ? typed.safeMessage
            : "Could not refresh the walkthrough. Check your connection, then try again."
        );
      }
    } finally {
      if (activeTokenRef.current === requestToken) setDownloadBusy(null);
    }
  };

  let content;
  if (status === "loading") {
    content = (
      <Centered as="output" aria-live="polite">
        Loading the shared listing prep…
      </Centered>
    );
  } else if (status !== "ok" || !doc) {
    content = (
      <Centered role="alert">
        <h1>This share link isn't available</h1>
        <p>
          {downloadError ||
            "It may have been revoked by its owner. Ask the agent for a current link."}
        </p>
      </Centered>
    );
  } else {
    const artifacts = doc.artifacts || {};
    const publicVideoPreview = publicApprovedVideoPreview(doc);
    const watermarked = doc.watermarked === true;
    const outputs = [
      ["report_pdf", "Listing Strategy (PDF)", "listing-strategy.pdf"],
      ["pptx", "PowerPoint", "listing-prep.pptx"],
      ["deck", "Seller Presentation (PDF)", "seller-presentation.pdf"],
      ["one_pager", "One-pager", "listing-one-pager.pdf"],
    ].flatMap(([key, label, filename]) => {
      const artifact = artifacts[key];
      return artifactUsable(artifact, key) && artifact.url ? [{ key, label, filename }] : [];
    });
    content = (
      <>
        {watermarked && (
          <Watermark aria-hidden="true" data-testid="listino-watermark">
            <span>Prepared with Listino</span>
          </Watermark>
        )}
        <Intro>
          <Eyebrow>Shared listing prep</Eyebrow>
          <Title>{doc.address || "Listing prep"}</Title>
          <Note>This owner-controlled link shows the version published by the listing agent.</Note>
        </Intro>
        {publicVideoPreview && (
          <VideoPreviewCard
            variant="public"
            preview={publicVideoPreview}
            externalBusy={downloadBusy === "video" ? "download" : null}
            onDownload={downloadVideo}
          />
        )}
        <Cover>
          <CoverPreview
            doc={doc}
            sections={doc.content?.sections || []}
            branding={doc.branding || {}}
            showViewDoc={false}
          />
        </Cover>
        <Downloads>
          {outputs.map((output) => (
            <div key={output.key}>
              <Download
                type="button"
                disabled={downloadBusy !== null}
                onClick={() => downloadArtifact(output)}
              >
                {downloadBusy === output.key ? "Refreshing…" : output.label}
              </Download>
              <Note>
                {output.label}: {artifactQualityMessage(artifacts[output.key], output.key)}
              </Note>
            </div>
          ))}
        </Downloads>
        {downloadError && <DownloadError role="alert">{downloadError}</DownloadError>}
        {watermarked && <WatermarkFooter>Prepared with Listino · listino.ai</WatermarkFooter>}
      </>
    );
  }

  return (
    <Page>
      <Helmet>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <NewNavbar />
      <Main>
        <Container style={{ maxWidth: 1100 }}>{content}</Container>
      </Main>
      <NewFooter />
    </Page>
  );
};

export default SharedReportPage;
